/**
 * 사용로그 · 에러로그 보기.
 *
 * 명세의 목적은 "개발자 혹은 AI 가 내용 확인하고 **분석 및 문제 조치**" 다. 그래서 목록을
 * 늘어놓기 전에 **무엇이 제일 많이 나는가**(요약)를 먼저 보여 준다 — 조치는 거기서 시작한다.
 *
 * ## 무엇이 안 보이는지도 보여 준다
 *
 *   · 기본은 **본인 것**이다. 전체를 보려면 `logsAll` 권한이 있어야 하고, 없는데 요청하면
 *     조용히 본인 것만 주지 않고 **거부됐다고 말한다**("내 것밖에 없네" 로 잘못 읽지 않게).
 *   · SQL·바인드·메시지는 **가려져 있다.** 몇 개를 가렸는지(`hiddenFields`)를 함께 그려
 *     "원래 없었다" 와 구분되게 한다.
 *   · 기록 수준이 `minimal` 이면 애초에 저장되지 않았다는 것도 적는다 — 가린 것과 없는 것은
 *     다르다.
 *
 * ## 기록 수준을 여기서 켠다
 *
 * `full` 은 내 SQL 과 바인드 **값**이 저장된다는 뜻이라, 무엇이 켜져 있는지 사용자가 늘 볼 수
 * 있어야 하고 **사람이 직접** 켜야 한다. 그래서 이 화면에만 둔다(도구·API 경로에는 없다).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { usePrompt } from '../PromptDialog';
// 로그 시각은 앱 저장소(SQLite)의 UTC 글이다 — 한국 시각으로 보여 준다(2026-10-22)
import { formatStoredTime, storedTime } from '../../utils/storedTime';
import useModalWindow from '../common/useModalWindow';
import { FiX, FiRefreshCw, FiAlertCircle, FiLoader, FiEye, FiEyeOff } from 'react-icons/fi';
import {
    fetchUseLogs, fetchErrorLogs, fetchLogGroups, fetchLogLevel, setLogLevel, fetchFeedback, feedbackImageUrl,
    setGroupStatus, markLogsSeen,
} from '../../services/opsLog';

const TABS = ['summary', 'errors', 'use', 'feedback'];
/** 조회 기간. 기본을 7일로 둔다 — 전부 보면 느리고, 조치는 최근 것부터다. */
const RANGES = [1, 7, 30];

const sinceOf = (days) => {
    const d = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
    return d.toISOString().slice(0, 19).replace('T', ' ');
};

/**
 * 문제 신고 목록 — 사용자가 쓴 글·오류 번호·직전 기능·맥락·첨부. 오류 번호를 누르면 그 기록으로 간다
 * (신고와 기록을 잇는 것이 이 목록의 요점이다).
 */
function FeedbackRows({ rows, all, onIncident, t }) {
    return (
        <table className="tm-table">
            <tbody>
                {rows.map(r => (
                    <tr key={r.id}>
                        <td className="ol-when">{formatStoredTime(r.createdAt)}</td>
                        {all ? <td className="ol-user">{r.usrid || '-'}</td> : null}
                        <td>
                            <div className="ol-feature">{r.id}</div>
                            {r.text ? <div className="ol-fbText">{r.text}</div> : null}
                            {r.incidentIds && r.incidentIds.length ? (
                                <div className="ol-incidents">
                                    {r.incidentIds.map(id => (
                                        <button
                                            key={id} type="button" className="ol-incident"
                                            onClick={() => onIncident(id)} title={t('assistant:opsLog.openIncident')}
                                        >
                                            {id}
                                        </button>
                                    ))}
                                </div>
                            ) : null}
                            {r.context && (r.context.release || r.context.browser || r.context.route) ? (
                                <div className="ta-meta">{[r.context.release, r.context.browser, r.context.route].filter(Boolean).join(' · ')}</div>
                            ) : null}
                            {r.actions && r.actions.length ? (
                                <details className="ol-actions">
                                    <summary>{t('assistant:opsLog.feedbackActions', { count: r.actions.length })}</summary>
                                    <div className="ol-stack">
                                        {r.actions.map(a => `${storedTime(a.at, 'time')}  ${a.action}`).join('\n')}
                                    </div>
                                </details>
                            ) : null}
                            {r.imageCount ? (
                                <div className="ol-fbImages">
                                    {Array.from({ length: r.imageCount }, (_x, i) => (
                                        <a key={i} href={feedbackImageUrl(r.id, i)} target="_blank" rel="noreferrer">
                                            <img src={feedbackImageUrl(r.id, i)} alt={t('assistant:opsLog.feedbackImage', { n: i + 1 })} />
                                        </a>
                                    ))}
                                </div>
                            ) : null}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

FeedbackRows.propTypes = {
    rows: PropTypes.arrayOf(PropTypes.object).isRequired,
    all: PropTypes.bool.isRequired,
    onIncident: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
};

/**
 * 오류 묶음 — 같은 오류를 한 줄로(opsLog/fingerprint.js). 재발이 맨 위다. 해결 표시는 관리자만 하고,
 * 해결 뒤에 다시 나면 서버가 재발로 바꾼다. 「기록 보기」는 그 묶음의 기록만 연다.
 */
function GroupRows({ rows, canResolve, onOpen, onStatus, t }) {
    return (
        <table className="tm-table">
            <tbody>
                {rows.map(g => (
                    <tr key={g.fingerprint} className={`ol-group ol-group-${g.status}`}>
                        <td className="ol-count">{g.count}</td>
                        <td>
                            <div className="ol-feature">
                                <span className={`ol-status ol-status-${g.status}`}>{t(`assistant:opsLog.groupStatus.${g.status}`)}</span>
                                <b>{g.errorType || t('assistant:opsLog.noType')}</b>
                                {g.oraCode || g.messageKind ? <span className="ta-meta">{g.oraCode || g.messageKind}</span> : null}
                                {g.origin === 'client' ? <span className="gm-dbms">client</span> : null}
                            </div>
                            <div className="ta-meta">{g.feature}</div>
                            {g.position ? <pre className="ol-stack">{g.position}</pre> : null}
                            <div className="ta-meta">
                                {t('assistant:opsLog.groupMeta', { users: g.users, first: formatStoredTime(g.first), last: formatStoredTime(g.last) })}
                                {g.releases && g.releases.length ? ` · ${g.releases.join(', ')}` : ''}
                            </div>
                            {g.resolvedAt ? (
                                <div className="tcf-hint">{t('assistant:opsLog.groupResolved', { at: formatStoredTime(g.resolvedAt), by: g.resolvedBy || '-' })}</div>
                            ) : null}
                        </td>
                        <td className="ol-groupActions">
                            <button type="button" className="tcf-btn" onClick={() => onOpen(g)}>{t('assistant:opsLog.groupRecords')}</button>
                            {canResolve ? (
                                <button type="button" className="tcf-btn" onClick={() => onStatus(g, g.status === 'resolved' ? 'open' : 'resolved')}>
                                    {g.status === 'resolved' ? t('assistant:opsLog.groupReopen') : t('assistant:opsLog.groupResolve')}
                                </button>
                            ) : null}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

GroupRows.propTypes = {
    rows: PropTypes.arrayOf(PropTypes.object).isRequired,
    canResolve: PropTypes.bool.isRequired,
    onOpen: PropTypes.func.isRequired,
    onStatus: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
};

function OpsLogModal({ open, onClose }) {
    const { t } = useTranslation();
    // 창처럼 — 작업 탭으로 열린다(2026-10-19, components/workspace/WorkspaceDock). 분리도 된다
    const win = useModalWindow({ title: t('assistant:opsLog.title'), onClose, icon: <FiAlertCircle size={13} /> });
    const prompt = usePrompt();
    const [tab, setTab] = useState('summary');
    const [days, setDays] = useState(7);
    const [all, setAll] = useState(false);
    const [sensitive, setSensitive] = useState(false);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [level, setLevel] = useState(null);
    const [error, setError] = useState(null);
    // 신고의 오류 번호를 눌렀을 때 — 그 번호의 기록만 본다(기간은 보지 않는다: 신고가 며칠 뒤에 올 수 있다)
    const [incident, setIncident] = useState(null);
    // 오류 묶음의 「기록 보기」 — 그 묶음의 기록만 본다(기간은 보지 않는다: 묶음에는 오래된 기록도 있다)
    const [group, setGroup] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const byFilter = tab === 'errors' && (incident || group);
            const params = { since: byFilter ? undefined : sinceOf(days), all: all || undefined, sensitive: sensitive || undefined, limit: 300 };
            const [r, lv] = await Promise.all([
                tab === 'summary' ? fetchLogGroups(params)
                    : tab === 'errors' ? fetchErrorLogs({
                        ...params,
                        incidentId: incident || undefined,
                        fingerprint: group ? group.fingerprint : undefined,
                    })
                        : tab === 'feedback' ? fetchFeedback(params) : fetchUseLogs(params),
                fetchLogLevel(),
            ]);
            setData(r);
            setLevel(lv);
        } catch (err) {
            setError({ message: err.message });
        } finally { setLoading(false); }
    }, [tab, days, all, sensitive, incident, group]);

    useEffect(() => { if (open) load(); }, [open, load]);

    // 관리자가 창을 열면 메뉴의 알림 표시(새 오류·재발·새 신고)를 지운다 — 한 번 열 때 한 번
    const canSeeAll = !!(level && level.canSeeAll);
    useEffect(() => { if (open && canSeeAll) markLogsSeen(); }, [open, canSeeAll]);

    const changeGroup = useCallback(async (g, next) => {
        try {
            await setGroupStatus(g.fingerprint, next);
            toast.success(t(next === 'resolved' ? 'assistant:opsLog.groupResolvedToast' : 'assistant:opsLog.groupReopenedToast'));
            load();
        } catch (err) { toast.error(err.message); }
    }, [t, load]);

    const changeLevel = useCallback(async (next) => {
        // 내 SQL 과 바인드 값이 저장된다는 뜻이라 반드시 묻는다(사유는 기록에 남는다) — 공통 입력 대화상자
        const reason = next === 'full' ? await prompt({
            title: t('assistant:opsLog.fullReasonTitle'),
            message: t('assistant:opsLog.fullReason'),
            confirmText: t('assistant:opsLog.fullReasonConfirm'),
        }) : null;
        if (next === 'full' && reason === null) return;      // 취소
        try {
            const r = await setLogLevel({ level: next, reason });
            setLevel(l => ({ ...(l || {}), level: r.level, history: r.history }));
            toast.success(t('assistant:opsLog.levelChanged'));
        } catch (err) { toast.error(err.message); }
    }, [t, prompt]);

    const rows = useMemo(() => (data && data.items) || [], [data]);

    if (!open) return null;
    return win.wrap(
        <div className="tcase-overlay" style={win.overlayStyle} onClick={() => { if (!win.popped && !win.maximized && !win.docked) onClose(); }} role="presentation">
            <div className="app-modal tcase-modal" data-app-modal onClick={(e) => e.stopPropagation()} role="presentation" ref={win.frameRef} style={win.frameStyle}>
                <div className="app-modal-header tcase-header" {...win.headerProps}>
                    <span className="app-modal-title tcase-title">
                        <FiAlertCircle size={14} /> {t('assistant:opsLog.title')}
                    </span>
                    <span className="tcase-headerRight">
                        <button type="button" className="tcase-new" onClick={load} disabled={loading}>
                            {loading ? <FiLoader size={12} className="tcf-spin" /> : <FiRefreshCw size={12} />}
                            {t('common:ui.refresh')}
                        </button>
                        {win.controls}
                        <button type="button" className="app-modal-close tcase-close" onClick={onClose} title={t('common:ui.close')}>
                            <FiX size={16} />
                        </button>
                    </span>
                </div>

                <div className="tcase-formWrap">
                    <div className="tcf">
                        {/* 기록 수준 — **지금 내 SQL 이 저장되고 있는지**를 늘 보이게 */}
                        <section className="tcf-step">
                            <div className="ol-levelRow">
                                <span className={`ol-level ol-level-${(level && level.level) || 'minimal'}`}>
                                    {t(`assistant:opsLog.level.${(level && level.level) || 'minimal'}`)}
                                </span>
                                <span className="tcf-hint">{t('assistant:opsLog.levelHint')}</span>
                                <button
                                    type="button" className="tcf-btn"
                                    onClick={() => changeLevel(level && level.level === 'full' ? 'minimal' : 'full')}
                                >
                                    {level && level.level === 'full' ? <FiEyeOff size={11} /> : <FiEye size={11} />}
                                    {t(level && level.level === 'full'
                                        ? 'assistant:opsLog.levelOff' : 'assistant:opsLog.levelOn')}
                                </button>
                            </div>
                            {level && level.history && level.history.length ? (
                                <div className="tcf-hint">
                                    {t('assistant:opsLog.lastChange', {
                                        level: level.history[0].level,
                                        by: level.history[0].set_by,
                                        at: formatStoredTime(level.history[0].set_at),
                                    })}
                                </div>
                            ) : null}
                        </section>

                        <section className="tcf-step">
                            <div className="tcf-row">
                                <div className="tcf-tabs">
                                    {TABS.map(id => (
                                        <button
                                            key={id} type="button"
                                            className={`tcf-tab${tab === id ? ' tcf-tabOn' : ''}`}
                                            onClick={() => { setTab(id); if (id !== 'errors') { setIncident(null); setGroup(null); } }}
                                        >
                                            {t(`assistant:opsLog.tab.${id}`)}
                                        </button>
                                    ))}
                                </div>
                                <select className="tcf-input ta-narrow" value={days} onChange={(e) => setDays(Number(e.target.value))}>
                                    {RANGES.map(d => (
                                        <option key={d} value={d}>{t('assistant:opsLog.lastDays', { days: d })}</option>
                                    ))}
                                </select>
                                {level && level.canSeeAll ? (
                                    <>
                                        <label className="tcf-choice">
                                            <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} />
                                            {t('assistant:opsLog.allUsers')}
                                        </label>
                                        <label className="tcf-choice">
                                            <input
                                                type="checkbox" checked={sensitive} disabled={!all}
                                                onChange={(e) => setSensitive(e.target.checked)}
                                            />
                                            {t('assistant:opsLog.showSensitive')}
                                        </label>
                                    </>
                                ) : null}
                            </div>
                            {/* 권한이 없는데 전체를 요청했으면 **거부됐다고 말한다** */}
                            {data && data.denied ? (
                                <div className="ta-gap">{t('assistant:opsLog.denied')}</div>
                            ) : null}
                            {/* 조직 관리자 — 전체가 아니라 **조직원 범위**다(민감 필드는 열리지 않는다) */}
                            {data && data.scope === 'org' ? (
                                <div className="ta-gap">{t('assistant:opsLog.orgScope')}</div>
                            ) : null}
                            {error ? <div className="tcf-leak"><FiAlertCircle size={12} /><div className="tcf-leakBody">{error.message}</div></div> : null}
                            {tab === 'errors' && incident ? (
                                <div className="ol-filter">
                                    {t('assistant:opsLog.incidentFilter', { id: incident })}
                                    <button type="button" className="tcf-btn" onClick={() => setIncident(null)}>{t('assistant:opsLog.clearFilter')}</button>
                                </div>
                            ) : null}
                            {tab === 'errors' && group ? (
                                <div className="ol-filter">
                                    {t('assistant:opsLog.groupFilter', { label: group.label })}
                                    <button type="button" className="tcf-btn" onClick={() => setGroup(null)}>{t('assistant:opsLog.clearFilter')}</button>
                                </div>
                            ) : null}
                        </section>

                        <section className="tcf-step">
                            {!rows.length && !loading ? <div className="tcf-hint">{t('assistant:opsLog.empty')}</div> : null}

                            {tab === 'summary' ? (
                                <GroupRows
                                    rows={rows} t={t}
                                    canResolve={!!(data && data.canResolve)}
                                    onStatus={changeGroup}
                                    onOpen={(g) => {
                                        setIncident(null);
                                        setGroup({ fingerprint: g.fingerprint, label: `${g.errorType || '-'} · ${g.position || g.feature || ''}` });
                                        setTab('errors');
                                    }}
                                />
                            ) : tab === 'feedback' ? (
                                <FeedbackRows rows={rows} all={all} t={t} onIncident={(id) => { setIncident(id); setTab('errors'); }} />
                            ) : (
                                <table className="tm-table">
                                    <tbody>
                                        {rows.map(r => (
                                            <tr key={r.id}>
                                                <td className="ol-when">{formatStoredTime(r.createdAt)}</td>
                                                {all ? <td className="ol-user">{r.usrid || '-'}</td> : null}
                                                <td>
                                                    <div className="ol-feature">
                                                        {r.feature}
                                                        {r.origin === 'client' ? <span className="gm-dbms">client</span> : null}
                                                        {r.incidentId ? <span className="ol-incidentTag">{r.incidentId}</span> : null}
                                                        {r.status ? <span className="ta-meta">{r.status}</span> : null}
                                                    </div>
                                                    {r.errorType ? <div className="ol-type">{r.errorType}{r.oraCode ? ` · ${r.oraCode}` : ''}</div> : null}
                                                    {/* 오류 종류(코드)·빌드·브라우저·화면 — 같은 오류가 어느 판·어느 브라우저에서 나는지 */}
                                                    {r.messageKind || r.release || r.browser || r.route ? (
                                                        <div className="ta-meta">{[r.messageKind, r.release, r.browser, r.route].filter(Boolean).join(' · ')}</div>
                                                    ) : null}
                                                    {r.message ? <div className="ol-msg">{r.message}</div> : null}
                                                    {r.sql ? <pre className="ta-ddl">{r.sql}</pre> : null}
                                                    {/* 소스맵으로 되돌린 **원래 위치**가 있으면 그것을 — 압축된 번들 위치는 사람이 못 읽는다 */}
                                                    {r.stackSrc || r.stackAt ? <pre className="ol-stack">{r.stackSrc || r.stackAt}</pre> : null}
                                                    {r.componentSrc || r.componentAt ? <pre className="ol-stack">{r.componentSrc || r.componentAt}</pre> : null}
                                                    {/* **가렸다는 사실**을 그린다 — "원래 없었다" 와 다르다 */}
                                                    {r.hiddenFields ? (
                                                        <div className="tcf-hint">
                                                            {t('assistant:opsLog.hidden', { count: r.hiddenFields })}
                                                        </div>
                                                    ) : null}
                                                </td>
                                                <td className="ol-dur">{r.durationMs != null ? `${r.durationMs}ms` : ''}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </section>
                    </div>
                </div>
            </div>
        </div>
    );
}

OpsLogModal.propTypes = { open: PropTypes.bool, onClose: PropTypes.func.isRequired };
OpsLogModal.defaultProps = { open: false };

export default OpsLogModal;
